import { Separator } from "@/src/components/ui/separator";
import { YachtData } from "./search";
import { getConfig } from "@/src/config";
import { cn, unwrapError } from "@/src/lib/utils";
import { ExternalLink } from "lucide-react";
import { Button } from "@/src/components/ui/button";
import { ReactNode } from "react";
import { BMOnHoldButton } from "./OnHoldButton";

export type YachtCardProps = {
  data: YachtData;
  className?: string;
  actions?: ReactNode;
};

export const YachtCard = (props: YachtCardProps) => {
  const {
    id,
    name,
    model,
    year,
    berths,
    slug,
    cabins,
    bathrooms,
    operator,
    operatorRating,
    brands,
    location,
    features,
    amenities,
    startTime,
    endTime,
    status,
    price,
    listPrice,
    deposit,
    pictures,
  } = props.data;
  const discountAmount = listPrice - price;
  const discountPercent = Math.round((discountAmount / listPrice) * 100);

  const renderEur = (p: number) => {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR" }).format(p);
  };

  const getTime = (d: string) => {
    const date = new Date(d);
    const day = date.getDate().toString();
    const month = date.toLocaleString("en-US", { month: "short" });
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day} ${month} ${hours}:${minutes}`;
  };

  return (
    <div className="flex flex-col space-y-4 p-4 bg-white rounded-md overflow-hidden">
      <div className="flex flex-row gap-6">
        <div className="w-fit">
          <img src={pictures[0]} alt={name} className="w-[250px] h-[150px] object-cover rounded-lg" />
        </div>
        <div className="w-fit flex flex-col gap-1.5 items-start">
          {status === "OPTIONED" && (
            <span className="inline-block bg-yellow-200 text-yellow-900 text-xs px-2 py-1 rounded">On option</span>
          )}

          <Button asChild className="text-black p-0 h-[28px]" variant="link">
            <a
              target="_blank"
              rel="noreferrer"
              href={`${unwrapError(() => getConfig().WEBSITE_URL, "")}/yacht/${slug}`}
            >
              <span className="max-w-[400px] overflow-hidden text-ellipsis"> “{name || "<No Name>"}”</span>
              <ExternalLink className="ml-1.5" size={16} />
            </a>
          </Button>
          <p>
            {model} | {year}
          </p>
          <p>
            {berths} people • {cabins} cabins • {bathrooms} bathrooms
          </p>

          <LabeledItem label="Operator">
            {operator} ({operatorRating}/10)
          </LabeledItem>
          {brands.length > 0 && <LabeledItem label="Brands">{brands.join(", ")}</LabeledItem>}
        </div>
        <div className="flex-1 flex flex-col justify-between items-end">
          <div className="flex flex-col items-end">
            <span className={cn("text-xs", discountAmount > 0 && "line-through")}>{renderEur(listPrice)}</span>
            {discountPercent > 0 && (
              <span className="text-xs text-green-600">{`Discount applied: ${renderEur(discountAmount)} (-${discountPercent}%)`}</span>
            )}
            <span className="text-xs">
              Discounted charter price: <span className="text-xl mx-1.5">{renderEur(price)}</span>
            </span>
            {deposit > 0 && <span className="text-xs">Deposit: {renderEur(deposit)}</span>}
          </div>
          {props.actions}
        </div>
      </div>

      <Separator orientation="horizontal" />

      <div className="flex flex-row">
        <div className="w-fit flex flex-row gap-8">
          <div className="w-48">
            <LabeledItem label="Check-In">{location}</LabeledItem>
            {getTime(startTime)}
          </div>
          <div className="w-48">
            <LabeledItem label="Check-Out">{location}</LabeledItem>
            {getTime(endTime)}
          </div>
        </div>
        <div className="w-fit">
          <Separator orientation="vertical" />
        </div>
        <div className="flex-1"></div>
      </div>
    </div>
  );
};

function LabeledItem({ label, children }: { label: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-slate-500 font-medium mt-0.5">{label}</span>
      <span>{children}</span>
    </div>
  );
}
